import {UserModel} from 'app/modules/auth'
import SchoolPage from 'app/pages/school/school/SchoolPage'
import {
  getTenantName,
  hasPermission,
  isAdmin,
  isDevEnv,
  isLyceum,
  isTeacher,
} from 'common/HelperFunctions'
import SuspensedView from 'common/components/SuspensedView'
import {ATTENDANCE_URL_FOR_BRANCH_SELECTION} from 'common/constants'
import {
  isAttendanceBetaEnabled,
  isHomeworkBetaEnabled,
  isHomeworkBetaToggleEnabled,
  isNewHomeworkDisabled,
} from 'common/helpers'
import IframeLayout from 'common/layout/IframeLayout'
import {usePostHog} from 'posthog-js/react'
import {lazy, useEffect} from 'react'
import {useSelector} from 'react-redux'
import {Navigate, Route, Routes, useLocation, useSearchParams} from 'react-router'
import {RootState} from 'setup'

import {MasterLayout} from '_metronic/layout/MasterLayout'

/*
 *     A
 *    A A
 *   AAAAA
 *  A     A
 * A       A
 */
const AccountTypePage = lazy(() => import('app/pages/school/AccountType/AccountTypePage'))
const AdmissionFormPage = lazy(() => import('app/pages/school/AdmissionForm/AdmissionFormPage'))
const AnnualCalendarUploadPage = lazy(
  () => import('app/pages/school/DataImport/Schedules/AnnualCalendarUploadPage')
)
const AttendancePage = lazy(() => import('app/pages/school/Attendance/AttendancePage'))
const AttendanceRemarksPage = lazy(
  () => import('app/pages/school/AttendanceRemarks/attendanceRemarksPage')
)
const TeacherStaffAttendanceReportPage = lazy(
  () => import('app/pages/school/Attendance/TeacherStaffAttendanceReport')
)
const TeacherSubjectAttendanceReportPage = lazy(
  () => import('app/pages/school/Attendance/TeacherSubjectAttendanceReport')
)
const AttendanceSettingPage = lazy(
  () => import('app/pages/school/Attendance/AttendanceSettingPage')
)
const AttendanceUploadPage = lazy(
  () => import('app/pages/school/Attendance/Upload/AttendanceUploadPage')
)

/*
 * BBBBB
 * B    B
 * BBBBB
 * B    B
 * BBBBB
 */
const BiometricCsvLog = lazy(() => import('app/pages/school/BiometricCsvLog/BiometricCsvLogPage'))
const BiometricDeviceLogPage = lazy(
  () => import('app/pages/school/BiometricDeviceLog/BiometricDeviceLogPage')
)
const BiometricLogPage = lazy(() => import('app/pages/school/BiometricLog/BiometricLogPage'))
const BookPage = lazy(() => import('app/pages/school/library/book/BookPage'))
const BookReservationPage = lazy(
  () => import('app/pages/school/library/bookReservation/BookReservationPage')
)
const BookSettingPage = lazy(() => import('app/pages/school/library/setting/SettingPage'))
const BranchPage = lazy(() => import('app/pages/school/Branch/BranchPage'))

/*
 * CCCCC
 * C
 * C
 * C
 * CCCCC
 */
const CategoryPage = lazy(() => import('app/pages/school/library/category/CategoryPage'))
const CertificatePage = lazy(() => import('app/pages/school/certificate/CertificatePage'))
const ChatPage = lazy(() => import('common/components/chat/ChatPage'))
const ClassPage = lazy(() => import('app/pages/school/Class/ClassPage'))
const ClientSecretsPage = lazy(() => import('app/pages/school/ClientSecrets/ClientSecretsPage'))
const ContactSupportPage = lazy(() => import('app/pages/school/ContactSupport/ContactSupportPage'))
const CstUploadPage = lazy(
  () => import('app/pages/school/DataImport/ClassSubjectTeacher/CstUploadPage')
)
const CustomNamesPage = lazy(() => import('app/pages/school/CustomNames/CustomNamesPage'))
const ChangeLogPage = lazy(() => import('app/pages/school/ChangeLog/ChangeLogPage'))

/*
 * DDDDD
 * D    D
 * D     D
 * D    D
 * DDDDD
 */
const Dashboard = lazy(() => import('app/pages/school/dashboard/Dashboard'))
const DatabaseLogPage = lazy(() => import('app/pages/school/DatabaseLog/DatabaseLogPage'))
const DepartmentPage = lazy(() => import('app/pages/school/department/DepartmentPage'))
const DigitalRequestPage = lazy(() => import('app/pages/school/digital-request/DigitalRequestPage'))

/*
 * EEEEEE
 * E
 * EEEEEE
 * E
 * EEEEEE
 */

const ExamPage = lazy(() => import('app/pages/school/Exam/ExamPage'))

/*
 * FFFFF
 * F
 * FFF
 * F
 * F
 */
const FileDownloadPage = lazy(() => import('app/pages/school/FileDownload/FileDownloadPage'))
const FormPage = lazy(() => import('app/pages/school/Forms/FormPage'))

/*
 * GGGGG
 * G
 * G  GGG
 * G    G
 * GGGGG
 */
const GradePage = lazy(() => import('app/pages/school/Grade/GradePage'))

/*
 * H    H
 * H    H
 * HHHHHH
 * H    H
 * H    H
 */
const HomeworkPage = lazy(() => import('app/pages/school/Homework/HomeworkPage'))

/*
 * IIIII
 *   I
 *   I
 *   I
 * IIIII
 */
const InactiveUserPage = lazy(() => import('app/pages/school/InactiveUser/InactiveUserPage'))
const InventoryPage = lazy(() => import('app/pages/school/Inventory/InventoryPage'))

/*
 * L
 * L
 * L
 * L
 * LLLLL
 */
const LabelsUploadPage = lazy(
  () => import('app/pages/school/DataImport/UserLabels/LabelsUploadPage')
)
const LecturePage = lazy(() => import('app/pages/school/Lecture/LecturePage'))
const LevelPage = lazy(() => import('app/pages/school/level/LevelPage'))

/*
 * N    N
 * NN   N
 * N N  N
 * N  N N
 * N   NN
 * N    N
 */
const NotificationLogPage = lazy(
  () => import('app/pages/school/NotificationLog/NotificationLogPage')
)
const NewsPage = lazy(() => import('app/pages/school/News/NewsPage'))

/*
 * OOOOOO
 * O    O
 * O    O
 * O    O
 * O    O
 * OOOOOO
 */

const ObservationPage = lazy(() => import('app/pages/school/Observations/ObservationPage'))

/*
 * PPPPPPP
 * P    P
 * PPPPPP
 * P
 * P
 */
const ParentPage = lazy(() => import('app/pages/school/Parent/ParentPage'))
const PerformanceReportsPage = lazy(
  () => import('app/pages/school/PerformanceReports/PerformanceReportsPage')
)
const ProfilePage = lazy(() => import('app/pages/common/profile/ProfilePage'))

/*
 *  QQQQQQ
 * Q      Q
 * Q      Q
 * Q      Q
 * Q   Q  Q
 *  QQQQQQ Q
 *
 */

const QuestionPage = lazy(() => import('app/pages/school/Question/QuestionsPage'))

/*
 * RRRRR
 * R    R
 * RRRRR
 * R R
 * R   R
 */
const ResourcePage = lazy(() => import('app/pages/school/resource/ResourcePage'))
const ResourceCategoryPage = lazy(
  () => import('app/pages/school/ResourceCategory/ResourceCategoryPage')
)
const ResourceSettingsPage = lazy(
  () => import('app/pages/school/ResourceSetting/ResourceSettingPage')
)
const RequestPage = lazy(() => import('app/pages/school/request/RequestPage'))
const RoleModulePermissionsPage = lazy(
  () => import('app/pages/school/RoleModulePermissions/RoleModulePermissionsPage')
)
const RoyaltyPage = lazy(() => import('app/pages/school/Royalty/RoyaltyPage'))

/*
 * SSSSS
 * S
 * SSSSS
 *      S
 * SSSSS
 */
const SchedulePage = lazy(() => import('app/pages/school/Schedule/SchedulePage'))
const SectionPage = lazy(() => import('app/pages/school/Section/SectionPage'))
const SettingsPage = lazy(() => import('app/pages/school/settings/GeneralSettingsPage'))
const SmsLogPage = lazy(() => import('app/pages/school/SmsLog/SmsLogPage'))
const StaffPage = lazy(() => import('app/pages/school/Staff/StaffPage'))
const StaffRolePage = lazy(() => import('app/pages/school/StaffRole/StaffRolePage'))
const SupportTicketPage = lazy(
  () => import('app/pages/school/TicktingFunctnality/SupportTicketPage')
)

const StoreItemsUploadPage = lazy(
  () => import('app/pages/school/DataImport/StoreItems/StoreItemsUploadPage')
)
const StudentAttendanceReport = lazy(
  () => import('app/pages/school/Attendance/StudentAttendanceReport')
)
const StudentPage = lazy(() => import('app/pages/school/Student/StudentPage'))
const StudentExamUploadPage = lazy(
  () => import('app/pages/school/DataImport/Academics/StudentExam/StudentExamUploadPage')
)
const StudentHomeworkUploadPage = lazy(
  () => import('app/pages/school/DataImport/Academics/StudentHomework/StudentHomeworkUploadPage')
)
const StudentParentUploadPage = lazy(
  () => import('app/pages/school/DataImport/StudentParent/StudentParentUploadPage')
)
const StudentTestUploadPage = lazy(
  () => import('app/pages/school/DataImport/Academics/StudentTest/StudentTestUploadPage')
)
const StudentSubjectAttendanceReport = lazy(
  () => import('app/pages/school/Attendance/StudentSubjectAttendanceReport')
)
const StudentSubjectCombinedAttendanceReport = lazy(
  () =>
    import('app/pages/school/Attendance/components/utils/StudentSubjectCombinedAttendanceReport')
)
const SubjectPage = lazy(() => import('app/pages/school/library/subject/SubjectPage'))
const SubjectsPage = lazy(() => import('app/pages/school/Subject/SubjectPage'))

/*
 * TTTTT
 *   T
 *   T
 *   T
 *   T
 */
const TagsCategoryPage = lazy(() => import('app/pages/school/tagsCategory/TagsCategoryPage'))
const TagsPage = lazy(() => import('app/pages/school/tags/TagsPage'))
const TagsUploadPage = lazy(() => import('app/pages/school/DataImport/Tags/TagsUploadPage'))
const TeacherPage = lazy(() => import('app/pages/school/Teacher/TeacherPage'))
const TestPage = lazy(() => import('app/pages/school/Test/TestPage'))

/*
 * U    U
 * U    U
 * U    U
 * U    U
 *  UUUU
 */
const UserCustomAttendanceSchedulePage = lazy(
  () => import('app/pages/school/Attendance/UserCustomAttendanceSchedulePage')
)

/*
 * V    V
 * V    V
 * V    V
 *  V  V
 *   VV
 */
const VendorPage = lazy(() => import('app/pages/school/vendor/VendorPage'))

/*
 * W       W
 * W       W
 * W       W
 * W   W   W
 *  W W W W
 *   W   W
 */
const WhatsappLogPage = lazy(() => import('app/pages/school/WhatsappLog/WhatsappLogPage'))

const M_LAYOUT = 'm_layout'

const SchoolToInstitute = () => {
  const location = useLocation()

  return (
    <Navigate
      to={
        location.pathname.startsWith('/school/')
          ? location.pathname.replace('/school/', '/institute/')
          : location.pathname
      }
    />
  )
}

const SchoolRoutes = () => {
  const userBranches = useSelector<RootState>(({mainReducer}) => mainReducer.userBranches) as any
  const posthog = usePostHog()
  const [searchParams] = useSearchParams()
  const user = useSelector<RootState>((state) => state.userReducer.user) as UserModel
  const userType = useSelector<RootState>((state) => state.userReducer.userType) as string
  const tenant = useSelector<RootState>((state) => state.userReducer.tenant) as string

  let Layout = MasterLayout

  const sessionHadMLayout = sessionStorage.getItem(M_LAYOUT)

  if ([searchParams.get('mlayout'), sessionHadMLayout].includes('true')) {
    Layout = IframeLayout

    if (searchParams.get('mlayout') == 'true') {
      sessionStorage.setItem(M_LAYOUT, 'true')
    }
  }

  const permittedBranches = useSelector<RootState, any[]>(
    ({mainReducer}) => mainReducer.userBranches
  ) as any

  if (
    !isTeacher() &&
    !ATTENDANCE_URL_FOR_BRANCH_SELECTION.includes(window.location.pathname) &&
    localStorage.getItem('attendance_branch')
  ) {
    localStorage.removeItem('attendance_branch')
    localStorage.removeItem('branch')
    window.location.reload()
  } else if (
    !isTeacher() &&
    userBranches?.length !== 0 &&
    ATTENDANCE_URL_FOR_BRANCH_SELECTION.includes(window.location.pathname) &&
    !localStorage.getItem('branch')
  ) {
    localStorage.setItem('attendance_branch', `${permittedBranches[0]?.id}`)
    localStorage.setItem('branch', `${permittedBranches[0]?.id}`)
    window.location.reload()
  }

  useEffect(() => {
    if (user.id) {
      posthog?.group('tenant', tenant)
      posthog?.identify(`${user.id}`, {
        ...user,
        userType,
        tenant,
      })
    }
  }, [posthog, tenant, user, userType])

  return (
    <Routes>
      <Route element={<Layout />}>
        {/* Redirect to Dashboard after success login/registration */}
        <Route path='auth/*' element={<Navigate to='/dashboard' />} />

        {/* Pages */}
        <Route path='dashboard' element={<Dashboard />} />

        <Route path='messages/*' element={<ChatPage />} />

        <Route
          path='profile/*'
          element={
            <SuspensedView>
              <ProfilePage />
            </SuspensedView>
          }
        />

        <Route path='institute'>
          {isDevEnv() && (
            <Route
              path='accountType/*'
              element={
                <SuspensedView>
                  <AccountTypePage />
                </SuspensedView>
              }
            />
          )}

          <Route
            path='attendance/biometricAttendanceUpload/*'
            element={
              <SuspensedView>
                <AttendanceUploadPage />
              </SuspensedView>
            }
          />

          {/* todo: remove this condition after testing */}
          {(isDevEnv() || isAttendanceBetaEnabled()) && (
            <>
              <Route
                path='attendance/setting/*'
                element={
                  <SuspensedView>
                    <AttendanceSettingPage />
                  </SuspensedView>
                }
              />

              <Route
                path='attendance/*'
                element={
                  <SuspensedView>
                    <AttendancePage />
                  </SuspensedView>
                }
              />

              <Route
                path='attendance/teacher-report/*'
                element={
                  <SuspensedView>
                    <TeacherStaffAttendanceReportPage />
                  </SuspensedView>
                }
              />

              <Route
                path='attendance/teacher-subject-report/*'
                element={
                  <SuspensedView>
                    <TeacherSubjectAttendanceReportPage />
                  </SuspensedView>
                }
              />

              <Route
                path='attendance/student-report/*'
                element={
                  <SuspensedView>
                    <StudentAttendanceReport />
                  </SuspensedView>
                }
              />

              <Route
                path='attendance/student-subject-report/*'
                element={
                  <SuspensedView>
                    <StudentSubjectAttendanceReport />
                  </SuspensedView>
                }
              />

              <Route
                path='user-custom-attendance-schedule/*'
                element={
                  <SuspensedView>
                    <UserCustomAttendanceSchedulePage />
                  </SuspensedView>
                }
              />

              <Route
                path='attendance-remarks/*'
                element={
                  <SuspensedView>
                    <AttendanceRemarksPage />
                  </SuspensedView>
                }
              />
            </>
          )}

          <Route
            path='attendance/student-subject/combined-report/'
            element={
              <SuspensedView>
                <StudentSubjectCombinedAttendanceReport />
              </SuspensedView>
            }
          />

          <Route
            path='admission/*'
            element={
              <SuspensedView>
                <AdmissionFormPage />
              </SuspensedView>
            }
          />

          <Route
            path='biometricCsvLog/*'
            element={
              <SuspensedView>
                <BiometricCsvLog />
              </SuspensedView>
            }
          />

          <Route
            path='biometricDeviceLog/*'
            element={
              <SuspensedView>
                <BiometricDeviceLogPage />
              </SuspensedView>
            }
          />

          <Route
            path='biometricLog/*'
            element={
              <SuspensedView>
                <BiometricLogPage />
              </SuspensedView>
            }
          />

          <Route
            path='book/*'
            element={
              <SuspensedView>
                <BookPage />
              </SuspensedView>
            }
          />

          <Route
            path='bookReservation/*'
            element={
              <SuspensedView>
                <BookReservationPage />
              </SuspensedView>
            }
          />

          <Route
            path='book-settings/*'
            element={
              <SuspensedView>
                <BookSettingPage />
              </SuspensedView>
            }
          />

          <Route
            path='branches/*'
            element={
              <SuspensedView>
                <BranchPage />
              </SuspensedView>
            }
          />

          <Route
            path='category/*'
            element={
              <SuspensedView>
                <CategoryPage />
              </SuspensedView>
            }
          />

          <Route
            path='certificates/*'
            element={
              <SuspensedView>
                <CertificatePage />
              </SuspensedView>
            }
          />

          <Route
            path='classes/*'
            element={
              <SuspensedView>
                <ClassPage />
              </SuspensedView>
            }
          />

          {isAdmin() && (
            <Route
              path='client-secrets/*'
              element={
                <SuspensedView>
                  <ClientSecretsPage />
                </SuspensedView>
              }
            />
          )}

          <Route
            path='contact-support'
            element={
              <SuspensedView>
                <ContactSupportPage />
              </SuspensedView>
            }
          />

          <Route
            path='custom-names/*'
            element={
              <SuspensedView>
                <CustomNamesPage />
              </SuspensedView>
            }
          />

          <Route
            path='data-import/tags/*'
            element={
              <SuspensedView>
                <TagsUploadPage />
              </SuspensedView>
            }
          />

          <Route
            path='data-import/user-labels/*'
            element={
              <SuspensedView>
                <LabelsUploadPage />
              </SuspensedView>
            }
          />

          <Route
            path='data-import/store-items/*'
            element={
              <SuspensedView>
                <StoreItemsUploadPage />
              </SuspensedView>
            }
          />

          <Route
            path='data-import/student-homework/*'
            element={
              <SuspensedView>
                <StudentHomeworkUploadPage />
              </SuspensedView>
            }
          />

          <Route
            path='data-import/student-test/*'
            element={
              <SuspensedView>
                <StudentTestUploadPage />
              </SuspensedView>
            }
          />

          <Route
            path='data-import/student-exam/*'
            element={
              <SuspensedView>
                <StudentExamUploadPage />
              </SuspensedView>
            }
          />

          <Route
            path='data-import/class-subject-teacher/*'
            element={
              <SuspensedView>
                <CstUploadPage />
              </SuspensedView>
            }
          />

          <Route
            path='data-import/student-parent/*'
            element={
              <SuspensedView>
                <StudentParentUploadPage />
              </SuspensedView>
            }
          />

          <Route
            path='data-import/annual-calendar/*'
            element={
              <SuspensedView>
                <AnnualCalendarUploadPage />
              </SuspensedView>
            }
          />

          <Route
            path='dBLog/admin'
            element={
              <SuspensedView>
                <DatabaseLogPage />
              </SuspensedView>
            }
          />

          <Route
            path='departments/*'
            element={
              <SuspensedView>
                <DepartmentPage />
              </SuspensedView>
            }
          />

          <Route
            path='digital-requests/*'
            element={
              <SuspensedView>
                <DigitalRequestPage />
              </SuspensedView>
            }
          />

          <Route
            path='fileDownloadTest/'
            element={
              <SuspensedView>
                <FileDownloadPage />
              </SuspensedView>
            }
          />

          <Route
            path='general-settings/*'
            element={
              <SuspensedView>
                <SettingsPage />
              </SuspensedView>
            }
          />

          {!isLyceum() && (
            <Route
              path='grades/*'
              element={
                <SuspensedView>
                  <GradePage />
                </SuspensedView>
              }
            />
          )}

          {((isHomeworkBetaEnabled() && isHomeworkBetaToggleEnabled()) ||
            !isNewHomeworkDisabled()) && (
            <Route
              path='homeworks/*'
              element={
                <SuspensedView>
                  <HomeworkPage />
                </SuspensedView>
              }
            />
          )}

          <Route
            path='inactive-users/*'
            element={
              <SuspensedView>
                <InactiveUserPage />
              </SuspensedView>
            }
          />

          {isDevEnv() && (
            <Route
              path='inventory/*'
              element={
                <SuspensedView>
                  <InventoryPage />
                </SuspensedView>
              }
            />
          )}

          <Route
            path='lectures-new/*'
            element={
              <SuspensedView>
                <LecturePage />
              </SuspensedView>
            }
          />

          <Route
            path='level/*'
            element={
              <SuspensedView>
                <LevelPage />
              </SuspensedView>
            }
          />

          <Route
            path='news/*'
            element={
              <SuspensedView>
                <NewsPage />
              </SuspensedView>
            }
          />
          <Route
            path='notificationLog/*'
            element={
              <SuspensedView>
                <NotificationLogPage />
              </SuspensedView>
            }
          />

          <Route
            path='parent/*'
            element={
              <SuspensedView>
                <ParentPage />
              </SuspensedView>
            }
          />

          <Route
            path='performance-reports/*'
            element={
              <SuspensedView>
                <PerformanceReportsPage />
              </SuspensedView>
            }
          />

          <Route
            path='resources/*'
            element={
              <SuspensedView>
                <ResourcePage />
              </SuspensedView>
            }
          />

          <Route
            path='resource-categories/*'
            element={
              <SuspensedView>
                <ResourceCategoryPage />
              </SuspensedView>
            }
          />

          <Route
            path='resource-settings/*'
            element={
              <SuspensedView>
                <ResourceSettingsPage />
              </SuspensedView>
            }
          />

          <Route
            path='requests/*'
            element={
              <SuspensedView>
                <RequestPage />
              </SuspensedView>
            }
          />

          <Route
            path='role-module-permissions/*'
            element={
              <SuspensedView>
                <RoleModulePermissionsPage />
              </SuspensedView>
            }
          />

          <Route
            path='schedule/*'
            element={
              <SuspensedView>
                <SchedulePage />
              </SuspensedView>
            }
          />

          {!isLyceum() && (
            <Route
              path='sections/*'
              element={
                <SuspensedView>
                  <SectionPage />
                </SuspensedView>
              }
            />
          )}

          <Route
            path='smsLog/*'
            element={
              <SuspensedView>
                <SmsLogPage />
              </SuspensedView>
            }
          />

          <Route
            path='staff/*'
            element={
              <SuspensedView>
                <StaffPage />
              </SuspensedView>
            }
          />
          <Route
            path='staff-role/*'
            element={
              <SuspensedView>
                <StaffRolePage />
              </SuspensedView>
            }
          />
          <Route
            path='student/*'
            element={
              <SuspensedView>
                <StudentPage />
              </SuspensedView>
            }
          />

          <Route
            path='subject/*'
            element={
              <SuspensedView>
                <SubjectPage />
              </SuspensedView>
            }
          />

          <Route
            path='subjects/*'
            element={
              <SuspensedView>
                <SubjectsPage />
              </SuspensedView>
            }
          />

          {hasPermission('tickets_manager') && (
            <Route
              path='support-tickets/*'
              element={
                <SuspensedView>
                  <SupportTicketPage />
                </SuspensedView>
              }
            />
          )}

          <Route
            path='tags/*'
            element={
              <SuspensedView>
                <TagsPage />
              </SuspensedView>
            }
          />

          <Route
            path='tag-categories/*'
            element={
              <SuspensedView>
                <TagsCategoryPage />
              </SuspensedView>
            }
          />
          <Route
            path='teacher/*'
            element={
              <SuspensedView>
                <TeacherPage />
              </SuspensedView>
            }
          />

          <Route
            path='vendors/*'
            element={
              <SuspensedView>
                <VendorPage />
              </SuspensedView>
            }
          />

          {isDevEnv() && (
            <Route
              path='tests/*'
              element={
                <SuspensedView>
                  <TestPage />
                </SuspensedView>
              }
            />
          )}

          {(isDevEnv() || ['tres'].includes(getTenantName())) && (
            <Route
              path='royalty/*'
              element={
                <SuspensedView>
                  <RoyaltyPage />
                </SuspensedView>
              }
            />
          )}

          {isDevEnv() && (
            <Route
              path='exam/*'
              element={
                <SuspensedView>
                  <ExamPage />
                </SuspensedView>
              }
            />
          )}

          {isDevEnv() && (
            <>
              <Route
                path='forms/*'
                element={
                  <SuspensedView>
                    <FormPage />
                  </SuspensedView>
                }
              />

              <Route
                path='observations/*'
                element={
                  <SuspensedView>
                    <ObservationPage />
                  </SuspensedView>
                }
              />

              <Route
                path='questions/*'
                element={
                  <SuspensedView>
                    <QuestionPage />
                  </SuspensedView>
                }
              />
            </>
          )}

          <Route
            path='change-log/*'
            element={
              <SuspensedView>
                <ChangeLogPage />
              </SuspensedView>
            }
          />

          <Route
            path='whatsappLog/*'
            element={
              <SuspensedView>
                <WhatsappLogPage />
              </SuspensedView>
            }
          />

          <Route path='*' element={<SchoolPage />} />
        </Route>

        <Route path='school/*' element={<SchoolToInstitute />} />

        {/* Page Not Found */}
        <Route path='*' element={<Navigate to='/error/404' />} />
      </Route>
    </Routes>
  )
}

export default SchoolRoutes
